import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import api from "../../../services/api";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export default function CoachHistoryDialog({
  coachNo,
  open: controlledOpen,
  onClose,
  buttonLabel = "Coach History",
  buttonProps = {},
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);
  const pdfContentRef = useRef(null);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const close = () => {
    if (!isControlled) setInternalOpen(false);
    onClose?.();
  };

  useEffect(() => {
    if (!open || !coachNo) return undefined;

    let active = true;
    setLoading(true);
    setError("");
    setData(null);

    const token = localStorage.getItem("token");
    api
      .get("/coach-history-all", {
        params: { coach_no: String(coachNo).trim() },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (active) setData(res.data);
      })
      .catch((err) => {
        if (active) {
          setError(
            err.response?.data?.message || "Could not load coach history",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, coachNo]);

  const events = useMemo(
    () =>
      [...(data?.timeline || [])].sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      }),
    [data],
  );

  const formatDate = (value) => {
    if (!value) return "—";
    const raw = String(value).trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    let date;

    if (match) {
      date = new Date(
        Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
      );
    } else {
      date = new Date(raw);
    }

    if (Number.isNaN(date.getTime())) return raw;
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = date.toLocaleString("en-GB", {
      month: "short",
      timeZone: "UTC",
    });
    return `${day}-${month}-${date.getUTCFullYear()}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatStructuredData = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "string") {
      try {
        value = JSON.parse(value);
      } catch {
        return value;
      }
    }

    const formatNested = (item, key = "") => {
      if (item === null || item === undefined) return null;
      if (item === "") return "";
      const dateField = /date|datetime|_at$|_on$|validfrom|validto/i.test(
        key.replace(/_/g, ""),
      );
      const isoDate =
        typeof item === "string" && /^\d{4}-\d{2}-\d{2}T/.test(item);
      if ((dateField || isoDate) && typeof item === "string") {
        return formatDateTime(item);
      }
      if (Array.isArray(item)) return item.map((entry) => formatNested(entry));
      if (typeof item === "object") {
        return Object.fromEntries(
          Object.entries(item).map(([childKey, childValue]) => [
            childKey,
            formatNested(childValue, childKey),
          ]),
        );
      }
      return item;
    };

    const formatted = formatNested(value);
    return typeof formatted === "object"
      ? JSON.stringify(formatted, null, 2)
      : String(formatted);
  };

  const renderValue = (value, key = "") => {
    if (value === null || value === undefined || value === "") return "—";
    const isDateField = /date|datetime|_at$|_on$|validfrom|validto/i.test(
      key.replace(/_/g, ""),
    );
    const isIsoDate =
      typeof value === "string" && /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value);

    if (isDateField || isIsoDate) {
      return /time|datetime|_at$/i.test(key) ||
        (typeof value === "string" && value.includes("T"))
        ? formatDateTime(value)
        : formatDate(value);
    }
    if (typeof value === "object") return formatStructuredData(value);
    return String(value);
  };

  const exportToPdf = async () => {
    const element = pdfContentRef.current;
    if (!element || exportingPdf) return;

    setExportingPdf(true);
    try {
      // Capture the full content, not only the currently visible scroll area.
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        windowWidth: Math.max(element.scrollWidth, element.clientWidth),
        onclone: (clonedDoc) => {
          const cloned = clonedDoc.querySelector("[data-coach-history-pdf]");
          if (cloned) {
            cloned.style.maxHeight = "none";
            cloned.style.height = "auto";
            cloned.style.overflow = "visible";
            cloned.style.width = `${element.scrollWidth}px`;
          }
        },
      });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerSpace = 15;
      const footerSpace = 10;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin - headerSpace - footerSpace;
      const pxPerMm = canvas.width / usableWidth;
      const sliceHeight = Math.floor(usableHeight * pxPerMm);

      let sourceY = 0;
      let pageNumber = 0;

      while (sourceY < canvas.height) {
        const currentSliceHeight = Math.min(sliceHeight, canvas.height - sourceY);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = currentSliceHeight;

        const context = pageCanvas.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        context.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          currentSliceHeight,
          0,
          0,
          canvas.width,
          currentSliceHeight,
        );

        if (pageNumber > 0) pdf.addPage();
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.text("Complete Coach History", margin, 8);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text(`Coach No: ${coachNo || "—"}`, margin, 12);

        const image = pageCanvas.toDataURL("image/jpeg", 0.95);
        const imageHeight = currentSliceHeight / pxPerMm;
        pdf.addImage(
          image,
          "JPEG",
          margin,
          margin + headerSpace,
          usableWidth,
          imageHeight,
        );

        pdf.setFontSize(8);
        pdf.text(
          `Page ${pageNumber + 1}`,
          pageWidth - margin,
          pageHeight - 5,
          { align: "right" },
        );

        sourceY += currentSliceHeight;
        pageNumber += 1;
      }

      const safeCoachNo = String(coachNo || "coach")
        .trim()
        .replace(/[^a-z0-9_-]+/gi, "_");
      pdf.save(`Coach_History_${safeCoachNo}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
      setError("Could not export PDF. Please try again.");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <>
      {!isControlled && (
        <Button
          size="small"
          startIcon={<HistoryIcon />}
          onClick={() => setInternalOpen(true)}
          {...buttonProps}
        >
          {buttonLabel}
        </Button>
      )}

      <Dialog
        open={Boolean(open)}
        onClose={close}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="h6" fontWeight={700}>
            Complete Coach History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Coach No: {coachNo || "—"}
          </Typography>
          <IconButton
            onClick={close}
            aria-label="close"
            sx={{ position: "absolute", right: 10, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent dividers>
          {loading && (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && data && (
            <Box
              ref={pdfContentRef}
              data-coach-history-pdf
              sx={{
                bgcolor: "#fff",
                color: "#202124",
                p: { xs: 0.5, sm: 1 },
                "& .pdf-section-title": { color: "#202124" },
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <Chip label={`Visits: ${data.summary?.visits ?? 0}`} />
                  <Chip
                    label={`Events: ${data.summary?.activities ?? events.length}`}
                  />
                  <Chip
                    label={`Status: ${data.summary?.latestVisitStatus || "—"}`}
                  />
                  <Chip
                    label={`First entry: ${formatDate(data.summary?.firstEntry)}`}
                  />
                </Stack>

                <Box>
                  <Typography
                    className="pdf-section-title"
                    variant="subtitle1"
                    fontWeight={700}
                    gutterBottom
                  >
                    Coach Details
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr 1fr",
                        sm: "repeat(3, 1fr)",
                      },
                      gap: 1,
                    }}
                  >
                    {[
                      ["Coach No", data.coach?.coach_no],
                      ["Type", data.coach?.coach_type_code],
                      ["Railway", data.coach?.railway_code],
                      ["Base", data.coach?.base_code],
                      ["Make", data.coach?.make_code],
                      ["Last POH", data.coach?.last_poh_date],
                      ["Last Out", data.coach?.last_out_date],
                      ["Repair Type", data.coach?.repair_type_code],
                    ].map(([label, value]) => (
                      <Box
                        key={label}
                        sx={{
                          p: 1,
                          bgcolor: "#f3f5f7",
                          borderRadius: 1,
                          minWidth: 0,
                          breakInside: "avoid",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          {["Last POH", "Last Out"].includes(label)
                            ? formatDate(value)
                            : renderValue(value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Divider />
                <Typography
                  className="pdf-section-title"
                  variant="subtitle1"
                  fontWeight={700}
                >
                  Activity Timeline
                </Typography>

                {events.length === 0 ? (
                  <Alert severity="info">
                    No history records found for this coach.
                  </Alert>
                ) : (
                  <Stack spacing={1.5}>
                    {events.map((event, index) => (
                      <Box
                        key={event.id || index}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          breakInside: "avoid",
                          borderBottom: "1px solid #e5e7eb",
                          pb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            sx={{
                              width: 11,
                              height: 11,
                              borderRadius: "50%",
                              bgcolor: "#1976d2",
                              mt: 0.7,
                              flexShrink: 0,
                            }}
                          />
                          {index < events.length - 1 && (
                            <Box
                              sx={{
                                width: 2,
                                flex: 1,
                                minHeight: 35,
                                bgcolor: "#d5dbe1",
                                mt: 0.5,
                              }}
                            />
                          )}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, pb: 1 }}>
                          <Stack
  direction="row"
  alignItems="center"
  justifyContent="flex-start"
  flexWrap="wrap"
  gap={1.5}
>
  <Typography fontWeight={700} variant="body2">
    {event.title || "History event"}
  </Typography>

  <Chip
    label={
      event.date
        ? formatDate(event.date)
        : "Date not recorded"
    }
    size="small"
    sx={{
      height: 25,
      borderRadius: "16px",
      bgcolor: "#eaf2ff",
      color: "#175cd3",
      fontWeight: 600,
      fontSize: "0.75rem",
      border: "1px solid #c7dcff",
      "& .MuiChip-label": {
        px: 1.2,
      },
    }}
  />
</Stack>

                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={0.5}
                            sx={{ my: 0.5 }}
                          >
                            {event.type && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={event.type}
                              />
                            )}
                            {event.visitNo && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Visit ${event.visitNo}`}
                              />
                            )}
                          </Stack>

                          {event.detail &&
                            Object.entries(event.detail).some(
                              ([, value]) =>
                                value !== null &&
                                value !== undefined &&
                                value !== "",
                            ) && (
                              <Box
                                sx={{
                                  p: 1.25,
                                  bgcolor: "#f5f6f8",
                                  borderRadius: 1,
                                }}
                              >
                                {Object.entries(event.detail)
                                  .filter(
                                    ([, value]) =>
                                      value !== null &&
                                      value !== undefined &&
                                      value !== "",
                                  )
                                  .map(([key, value]) => (
                                    <Box
                                      key={key}
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                          xs: "minmax(100px, 38%) 1fr",
                                          sm: "180px 1fr",
                                        },
                                        gap: 1,
                                        py: 0.25,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          textTransform: "capitalize",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {key
                                          .replace(/([A-Z])/g, " $1")
                                          .replace(/([a-z])([A-Z])/g, "$1 $2")}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {renderValue(value, key)}
                                      </Typography>
                                    </Box>
                                  ))}
                              </Box>
                            )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={close} color="inherit">
            Close
          </Button>
          <Button
            onClick={exportToPdf}
            variant="contained"
            startIcon={
              exportingPdf ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PictureAsPdfIcon />
              )
            }
            disabled={loading || !data || exportingPdf || Boolean(error)}
          >
            {exportingPdf ? "Preparing PDF..." : "Export PDF"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
